import { FC, useState, useCallback } from 'react'; // Changed useMemo to useCallback
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import { EffectCoverflow } from 'swiper/modules';
import JokeCard from './JokeCard';
import './JokesSwipeWrapper.scss';
import Countdown from '@moment/components/Jokes/Countdown.tsx';
import { useTranslation } from 'react-i18next';

// Define the shape of a single joke
interface Joke {
  id: string | number;
  setup: string;
  punchline: string;
}

// Define the props for the wrapper component
interface JokesSwipeWrapperProps {
  jokes: Joke[];
}

const JokesSwipeWrapper: FC<JokesSwipeWrapperProps> = ({ jokes }) => {

  // 1. State to track the index of the currently centered slide
  const [activeIndex, setActiveIndex] = useState(0);
  // 2. State to track the revealed status of each joke by its ID
  const [revealedJokes, setRevealedJokes] = useState<
    Record<string | number, boolean>
  >({});

  const { t } = useTranslation();

  // 3. Callback for the countdown to trigger the reveal of the active joke
  const onCountdownComplete = useCallback(() => {
    const activeJoke = jokes[activeIndex];
    if (activeJoke) {
      setRevealedJokes((prev) => ({
        ...prev,
        [activeJoke.id]: true, // Set the active joke to be revealed
      }));
    }
  }, [activeIndex, jokes]);

  // A simple fallback if there are no jokes to display
  if (!jokes || jokes.length === 0) {
    return <div className="jokes-swipe-wrapper--empty">{t('anErrorHasOccurred')}</div>;
  }

  // 4. Handler for when a user clicks a card to toggle its state
  const handleToggleReveal = (jokeId: string | number) => {
    setRevealedJokes((prev) => ({
      ...prev,
      [jokeId]: !prev[jokeId], // Toggle the reveal state
    }));
  };

  // Determine if the currently active joke is already revealed.
  const activeJoke = jokes[activeIndex];
  const isCurrentJokeRevealed = activeJoke
    ? !!revealedJokes[activeJoke.id]
    : false;

  return (
    <div className="jokes-swipe-wrapper">
      <div className="jokes-swipe-wrapper__header">
        <h2 className="jokes-swipe-wrapper__title header-md">{t('jokes')}</h2>
      </div>
      <Swiper
        className="jokes-swipe-wrapper__swiper"
        slidesPerView={'auto'}
        spaceBetween={16}
        centeredSlides={true}
        loop={true}
        grabCursor={true}
        effect={'coverflow'}
        modules={[EffectCoverflow]}
        coverflowEffect={{
          scale: 0.8,
          rotate: 0,
          stretch: 5,
          depth: 100,
          modifier: 1,
          slideShadows: false,
        }}
        // 5. Update the active index whenever the slide changes
        onSlideChange={(swiper) => {
          // `realIndex` is important for loop mode to get the correct array index
          setActiveIndex(swiper.realIndex);
        }}
      >
        {jokes.map((joke) => (
          <SwiperSlide key={joke.id} className="jokes-swipe-wrapper__slide">
            {/* 6. Pass the state and handler down to the JokeCard */}
            <JokeCard
              setup={joke.setup}
              punchline={joke.punchline}
              isRevealed={!!revealedJokes[joke.id]}
              onToggleReveal={() => handleToggleReveal(joke.id)}
            />
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Only show the countdown if the current joke is NOT revealed */}
      {!isCurrentJokeRevealed && (
        <Countdown
          // By setting the key to the activeIndex, React will re-mount the
          // Countdown component whenever the slide changes, resetting its timer.
          key={activeIndex}
          className="jokes-swipe-wrapper__countdown"
          duration={5}
          onComplete={onCountdownComplete}
        />
      )}
    </div>
  );
};

export default JokesSwipeWrapper;
